import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    Empty,
    Pagination,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, EnvironmentOutlined, MinusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { listExceptionalOffers, listMarketPrices } from "../../servics/trading/marketApi";
import { formatMoney } from "../../utils/money";
import { formatDate } from "../../constants/marketplaceCheckout";
import { subCategoryLabel } from "../../constants/subCategoryLabels";
import getProductImage, { fallbackToDefaultImage } from "../../utils/getProductImage";
import LoginToUnlock, { isLocked, useLoggedIn } from "./LoginToUnlock";
import { CROP_SUB_CATEGORIES, humanize, productTitle } from "./AvailableProducers";

const { Title, Text, Paragraph } = Typography;

const PAGE_SIZE = 12;

/** Tendance du cours calculée par price-tracker (régression sur l'historique des cours). */
export const TREND = {
    UP: { label: "Cours en hausse", color: "red", icon: <ArrowUpOutlined /> },
    DOWN: { label: "Cours en baisse", color: "green", icon: <ArrowDownOutlined /> },
    STABLE: { label: "Cours stable", color: "default", icon: <MinusOutlined /> },
    UNKNOWN: { label: "Tendance inconnue", color: "default", icon: null },
};

const quantity = (value, unit) =>
    `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(value))} ${unit || ""}`.trim();

const perUnit = (amount, currency, unit) => `${formatMoney(amount, currency?.code)} / ${unit}`;

const percent = (value) => `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(Number(value))} %`;

/**
 * Spéculateurs : offres exceptionnelles des producteurs, c'est-à-dire leurs mises en vente
 * (lots du catalogue, préventes) nettement sous le cours du marché du pays d'activité.
 *
 * La comparaison est faite par sales-and-market-tracking et son service price-tracker : la page
 * affiche l'écart, l'économie par unité, la tendance du cours et le score qu'ils renvoient, et
 * signale un cours provisoire (valeurs fixes de démarrage) comme tel.
 */
const AvailableSpeculators = () => {
    const navigate = useNavigate();
    const activityCountry = useSelector((state) => state.country.activityCountry);
    const countryCode = activityCountry?.value;
    const loggedIn = useLoggedIn();

    const [subCategory, setSubCategory] = useState(null);
    const [page, setPage] = useState(1);
    const [result, setResult] = useState({ items: [], total: 0, comparedOffers: 0, thresholdPercent: null });
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!countryCode) return;
        let cancelled = false;
        listMarketPrices(countryCode)
            .then((data) => {
                if (!cancelled) setPrices(data);
            })
            .catch(() => {
                // la liste des cours n'est qu'un complément : les offres restent affichées sans elle
                if (!cancelled) setPrices([]);
            });
        return () => {
            cancelled = true;
        };
    }, [countryCode, reloadKey]);

    useEffect(() => {
        if (!countryCode) return;
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        listExceptionalOffers({ country: countryCode, subCategory, page: page - 1, size: PAGE_SIZE })
            .then((data) => {
                if (!cancelled) setResult(data);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error(error);
                setResult({ items: [], total: 0, comparedOffers: 0, thresholdPercent: null });
                setLoadError(error?.response?.data?.message
                    || "Impossible de charger les offres exceptionnelles pour le moment.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [countryCode, subCategory, page, reloadKey]);

    const countryLabel = activityCountry?.label ? ` (${activityCountry.label})` : "";
    const provisional = prices.some((p) => p.provisional) || result.items.some((o) => o.provisionalReference);
    const threshold = result.thresholdPercent != null ? percent(result.thresholdPercent) : null;

    const openOffer = (offer) => {
        if (offer.kind === "PRESALE") {
            navigate("/producers");
        } else {
            navigate(`/catalog/category/${offer.subCategoryName}`);
        }
    };

    const emptyMessage = () => {
        if (prices.length === 0) {
            return `Aucun cours de référence n'est encore publié${activityCountry?.label ? ` pour ${activityCountry.label}` : ""} : les offres ne peuvent pas encore être comparées.`;
        }
        if (result.comparedOffers === 0) {
            return "Aucune mise en vente de producteur ne correspond aux produits cotés pour le moment.";
        }
        return `Aucune offre n'est actuellement ${threshold ? `au moins ${threshold}` : "nettement"} sous le cours du marché (${result.comparedOffers} offre(s) comparée(s)).`;
    };

    return (
        <div style={{ padding: 24 }}>
            <Title level={3}>Offres exceptionnelles des producteurs{countryLabel}</Title>
            <Paragraph type="secondary">
                Mises en vente des producteurs {threshold ? `au moins ${threshold}` : "nettement"} sous le cours du
                marché du pays : lots disponibles tout de suite et cultures en prévente.
            </Paragraph>

            {provisional && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="info"
                    showIcon
                    title="Cours de référence provisoires"
                    description="Les cours utilisés sont des valeurs fixes de démarrage, en attendant leur mise à jour deux fois par semaine."
                />
            )}

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8}>
                    <Select
                        placeholder="Toutes les cultures"
                        allowClear
                        style={{ width: "100%" }}
                        value={subCategory}
                        onChange={(value) => {
                            setSubCategory(value ?? null);
                            setPage(1);
                        }}
                        options={CROP_SUB_CATEGORIES.map((code) => ({ value: code, label: humanize(subCategoryLabel(code)) }))}
                        aria-label="Filtrer par culture"
                    />
                </Col>
            </Row>

            {loadError && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="error"
                    showIcon
                    title={loadError}
                    action={<Button size="small" onClick={() => setReloadKey((key) => key + 1)}>Réessayer</Button>}
                />
            )}

            <Spin spinning={loading}>
                {result.items.length === 0 && !loading && !loadError ? (
                    <Empty description={emptyMessage()} />
                ) : (
                    <Row gutter={[16, 16]}>
                        {result.items.map((offer, index) => {
                            const trend = TREND[offer.trend] || TREND.UNKNOWN;
                            return (
                                <Col xs={24} sm={12} md={8} lg={6} key={`${offer.kind}-${offer.offerId}`}>
                                    <LoginToUnlock locked={isLocked(loggedIn, page, index)}>
                                        <Card
                                            hoverable
                                            cover={
                                                <img
                                                    alt={productTitle(offer)}
                                                    src={getProductImage(offer.productName, offer.subCategoryName, offer.categoryName)}
                                                    onError={fallbackToDefaultImage}
                                                    style={{ height: 140, objectFit: "cover" }}
                                                />
                                            }
                                            title={<Text strong>{productTitle(offer)}</Text>}
                                            extra={<Tag color="volcano">-{percent(offer.discountPercent)}</Tag>}
                                        >
                                            <Space direction="vertical" style={{ width: "100%" }} size={4}>
                                                <Text>{offer.companyName}</Text>
                                                <Space wrap size={4}>
                                                    <Tag color={offer.kind === "PRESALE" ? "purple" : "green"}>
                                                        {offer.kind === "PRESALE" ? "Prévente" : "Disponible"}
                                                    </Tag>
                                                    {offer.city && <Tag icon={<EnvironmentOutlined />} color="blue">{offer.city}</Tag>}
                                                </Space>
                                                <Text strong>{perUnit(offer.unitPrice, offer.currency, offer.measurementUnit)}</Text>
                                                <Text type="secondary">
                                                    Cours {perUnit(offer.referencePrice, offer.currency, offer.measurementUnit)}
                                                    {offer.provisionalReference ? " (provisoire)" : ""}
                                                </Text>
                                                <Text>Économie : {perUnit(offer.savingPerUnit, offer.currency, offer.measurementUnit)}</Text>
                                                <Space wrap size={4}>
                                                    <Tag color={trend.color} icon={trend.icon}>{trend.label}</Tag>
                                                    <Tooltip title="Score d'opportunité : écart au cours et tendance">
                                                        <Tag color="gold">Score {offer.score}/100</Tag>
                                                    </Tooltip>
                                                </Space>
                                                <Text type="secondary">📦 {quantity(offer.availableQuantity, offer.measurementUnit)}</Text>
                                                {offer.kind === "PRESALE" && offer.availableFrom && (
                                                    <Text type="secondary">
                                                        Récolte prévue : {formatDate(offer.availableFrom)} · demandes jusqu&apos;au {formatDate(offer.requestDeadline)}
                                                    </Text>
                                                )}
                                            </Space>
                                            <Button block type="primary" style={{ marginTop: 12 }} onClick={() => openOffer(offer)}>
                                                {offer.kind === "PRESALE" ? "Voir les préventes" : "Voir le lot dans le catalogue"}
                                            </Button>
                                        </Card>
                                    </LoginToUnlock>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Spin>

            {result.total > PAGE_SIZE && (
                <Row justify="center" style={{ marginTop: 24 }}>
                    <Pagination current={page} pageSize={PAGE_SIZE} total={result.total} onChange={setPage} showSizeChanger={false} />
                </Row>
            )}

            {prices.length > 0 && (
                <Collapse
                    style={{ marginTop: 32 }}
                    items={[{
                        key: "prices",
                        label: `Cours de référence${countryLabel} (${prices.length})`,
                        children: (
                            <Table
                                size="small"
                                rowKey={(p) => `${p.productName}-${p.measurementUnit}`}
                                pagination={false}
                                dataSource={prices}
                                columns={[
                                    { title: "Produit", key: "product", render: (_, p) => humanize(p.productName) },
                                    { title: "Cours", key: "price", render: (_, p) => perUnit(p.unitPrice, p.currency, p.measurementUnit) },
                                    { title: "En vigueur depuis", dataIndex: "effectiveDate", key: "date", render: formatDate },
                                    { title: "", key: "source", render: (_, p) => (p.provisional ? <Tag>Provisoire</Tag> : null) },
                                ]}
                            />
                        ),
                    }]}
                />
            )}
        </div>
    );
};

export default AvailableSpeculators;
